import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import React, { useEffect, useMemo, useState } from 'react';

import type { KubernetesConsoleApi } from './api';
import {
  SilenceForm,
  matchersFromLabels,
  prefillFromSilence,
  type SilenceFormPrefill,
} from './SilenceForm';
import type {
  AlertmanagerAlert,
  AlertmanagerCapability,
  AlertmanagerSilence,
} from './types';

const PANEL = {
  border: '1px solid #4f5255',
  borderRadius: 2,
  bgcolor: '#1f1f1f',
  p: 2,
};

const SEVERITY_COLOR: Record<string, string> = {
  critical: '#F0561D',
  warning: '#F4C145',
  info: '#8BC1F7',
  none: '#C7C7C7',
};

const STATE_COLOR: Record<string, string> = {
  active: '#F0561D',
  pending: '#F4C145',
  suppressed: '#C7C7C7',
  unprocessed: '#8BC1F7',
  expired: '#6a6d70',
};

function severityColor(severity?: string): string {
  return SEVERITY_COLOR[severity ?? 'none'] ?? SEVERITY_COLOR.none;
}

function stateColor(state?: string): string {
  return STATE_COLOR[state ?? 'active'] ?? STATE_COLOR.active;
}

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 20,
        border: `1px solid ${color}55`,
        bgcolor: `${color}18`,
        color,
        fontSize: 10.5,
        fontWeight: 700,
        textTransform: 'uppercase',
      }}
    />
  );
}

function formatTime(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

// ─── alert grouping ──────────────────────────────────────────────────────────

interface AlertGroup {
  key: string;
  alertname: string;
  severity: string;
  state: string;
  count: number;
  instances: AlertmanagerAlert[];
  summary?: string;
  silencedBy: string[];
}

function groupAlerts(alerts: AlertmanagerAlert[]): AlertGroup[] {
  const groups = new Map<string, AlertGroup>();
  for (const alert of alerts) {
    const alertname = alert.labels.alertname ?? 'UnknownAlert';
    const severity = alert.severity ?? alert.labels.severity ?? 'none';
    const state = alert.state ?? 'active';
    const key = `${alertname}|${severity}|${state}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        alertname,
        severity,
        state,
        count: 0,
        instances: [],
        silencedBy: [],
      };
      groups.set(key, group);
    }
    group.count += 1;
    group.instances.push(alert);
    if (!group.summary) {
      group.summary =
        alert.annotations?.summary ?? alert.annotations?.description;
    }
    for (const fingerprint of alert.silencedBy ?? []) {
      if (!group.silencedBy.includes(fingerprint)) {
        group.silencedBy.push(fingerprint);
      }
    }
  }
  const severityRank = (severity: string) =>
    severity === 'critical' ? 0 : severity === 'warning' ? 1 : 2;
  return [...groups.values()].sort(
    (a, b) =>
      severityRank(a.severity) - severityRank(b.severity) ||
      a.alertname.localeCompare(b.alertname),
  );
}

/** Labels present, with the same value, on every instance of a group. */
function commonLabels(instances: AlertmanagerAlert[]): Record<string, string> {
  if (instances.length === 0) return {};
  const [first, ...rest] = instances;
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(first.labels)) {
    if (rest.every(instance => instance.labels[key] === value)) {
      result[key] = value;
    }
  }
  return result;
}

// ─── alert list ──────────────────────────────────────────────────────────────

function AlertRow({
  group,
  onSilence,
}: {
  group: AlertGroup;
  onSilence: (labels: Record<string, string>) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const showExpand = group.count > 1;
  return (
    <>
      <TableRow
        hover
        sx={{ cursor: showExpand ? 'pointer' : 'default' }}
        onClick={() => showExpand && setExpanded(value => !value)}
      >
        <TableCell sx={{ width: 32 }}>
          {showExpand ? (
            expanded ? (
              <ExpandLessIcon fontSize="small" sx={{ color: '#c7c7c7' }} />
            ) : (
              <ExpandMoreIcon fontSize="small" sx={{ color: '#c7c7c7' }} />
            )
          ) : null}
        </TableCell>
        <TableCell>
          <Badge label={group.severity} color={severityColor(group.severity)} />
        </TableCell>
        <TableCell>
          <Typography sx={{ color: '#f0f0f0', fontWeight: 700, fontSize: 13 }}>
            {group.alertname}
          </Typography>
          {group.summary && (
            <Typography sx={{ color: '#c7c7c7', fontSize: 11.5 }}>
              {group.summary}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Badge label={group.state} color={stateColor(group.state)} />
        </TableCell>
        <TableCell align="right">
          {group.count > 1 ? (
            <Chip size="small" label={`x${group.count}`} variant="outlined" />
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell align="right">
          {group.silencedBy.length > 0 ? (
            <Typography sx={{ color: '#c7c7c7', fontSize: 11 }}>
              {group.silencedBy.length} silence
              {group.silencedBy.length > 1 ? 's' : ''}
            </Typography>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell align="right" onClick={event => event.stopPropagation()}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onSilence(commonLabels(group.instances))}
          >
            Silence
          </Button>
        </TableCell>
      </TableRow>
      {expanded &&
        group.instances.map(instance => (
          <AlertInstanceRow
            key={instance.fingerprint}
            alert={instance}
            onSilence={onSilence}
          />
        ))}
    </>
  );
}

function AlertInstanceRow({
  alert,
  onSilence,
}: {
  alert: AlertmanagerAlert;
  onSilence: (labels: Record<string, string>) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', bgcolor: '#181818' }}
        onClick={() => setOpen(value => !value)}
      >
        <TableCell />
        <TableCell colSpan={2}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {Object.entries(alert.labels)
              .filter(([key]) => key !== 'alertname')
              .map(([key, value]) => (
                <Chip
                  key={key}
                  size="small"
                  label={`${key}=${value}`}
                  sx={{ fontSize: 10, height: 18 }}
                />
              ))}
          </Stack>
        </TableCell>
        <TableCell>
          {alert.state && (
            <Badge label={alert.state} color={stateColor(alert.state)} />
          )}
        </TableCell>
        <TableCell align="right" colSpan={2}>
          <Typography sx={{ color: '#c7c7c7', fontSize: 11 }}>
            since {formatTime(alert.startsAt)}
          </Typography>
        </TableCell>
        <TableCell align="right" onClick={event => event.stopPropagation()}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onSilence(alert.labels)}
          >
            Silence
          </Button>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow sx={{ bgcolor: '#161616' }}>
          <TableCell />
          <TableCell colSpan={6}>
            <Stack spacing={1} sx={{ py: 1 }}>
              <Box>
                <Typography
                  sx={{ color: '#c7c7c7', fontSize: 11, fontWeight: 700 }}
                >
                  Annotations
                </Typography>
                {Object.entries(alert.annotations ?? {}).length === 0 ? (
                  <Typography sx={{ color: '#c7c7c7', fontSize: 12 }}>
                    None.
                  </Typography>
                ) : (
                  Object.entries(alert.annotations).map(([key, value]) => (
                    <Typography
                      key={key}
                      sx={{ color: '#f0f0f0', fontSize: 12 }}
                    >
                      <strong>{key}:</strong>{' '}
                      {key === 'runbook_url' ? (
                        <a
                          href={value}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#8bc1f7' }}
                        >
                          {value}
                        </a>
                      ) : (
                        value
                      )}
                    </Typography>
                  ))
                )}
              </Box>
              {alert.generatorURL && (
                <Typography sx={{ fontSize: 12 }}>
                  <a
                    href={alert.generatorURL}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#8bc1f7' }}
                  >
                    Open in Prometheus
                  </a>
                </Typography>
              )}
              {alert.silencedBy.length > 0 && (
                <Box>
                  <Typography
                    sx={{ color: '#c7c7c7', fontSize: 11, fontWeight: 700 }}
                  >
                    Silenced by
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {alert.silencedBy.map(fingerprint => (
                      <Chip
                        key={fingerprint}
                        size="small"
                        label={fingerprint}
                        sx={{ fontFamily: 'monospace', fontSize: 10 }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function AlertsList({
  alerts,
  onSilence,
  onCreateSilence,
}: {
  alerts: AlertmanagerAlert[];
  onSilence: (labels: Record<string, string>) => void;
  onCreateSilence: () => void;
}) {
  const [severity, setSeverity] = useState('all');
  const [state, setState] = useState('all');
  const [search, setSearch] = useState('');

  const groups = useMemo(() => groupAlerts(alerts), [alerts]);
  const filtered = groups.filter(group => {
    if (severity !== 'all' && group.severity !== severity) return false;
    if (state !== 'all' && group.state !== state) return false;
    if (
      search.trim() &&
      !group.alertname.toLowerCase().includes(search.trim().toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  return (
    <Stack spacing={2}>
      <Box sx={PANEL}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            select
            size="small"
            label="Severity"
            value={severity}
            onChange={event => setSeverity(event.target.value)}
            sx={{ width: 160 }}
          >
            <MenuItem value="all">All severities</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="info">Info</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="State"
            value={state}
            onChange={event => setState(event.target.value)}
            sx={{ width: 160 }}
          >
            <MenuItem value="all">All states</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="suppressed">Suppressed</MenuItem>
            <MenuItem value="unprocessed">Unprocessed</MenuItem>
          </TextField>
          <TextField
            size="small"
            label="Search alert name"
            value={search}
            onChange={event => setSearch(event.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Box sx={{ flex: { md: 1 } }} />
          <Button variant="contained" onClick={onCreateSilence}>
            Create silence
          </Button>
        </Stack>
      </Box>
      <Box sx={PANEL}>
        {filtered.length === 0 ? (
          <Typography sx={{ color: '#c7c7c7', fontSize: 12 }}>
            No alerts match the current filters.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 32 }} />
                  <TableCell>Severity</TableCell>
                  <TableCell>Alert</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Silences</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(group => (
                  <AlertRow
                    key={group.key}
                    group={group}
                    onSilence={onSilence}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Stack>
  );
}

// ─── silence list ────────────────────────────────────────────────────────────

function SilenceMatchers({ silence }: { silence: AlertmanagerSilence }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap">
      {silence.matchers.map((matcher, index) => (
        <Chip
          key={`${matcher.name}-${index}`}
          size="small"
          label={`${matcher.name}${matcher.isEqual === false ? '!=' : '='}${
            matcher.isRegex ? '~' : ''
          }${matcher.value}`}
          sx={{ fontSize: 10, height: 20 }}
        />
      ))}
    </Stack>
  );
}

function SilencesList({
  silences,
  onExpire,
  onRecreate,
}: {
  silences: AlertmanagerSilence[];
  onExpire: (silence: AlertmanagerSilence) => void;
  onRecreate: (silence: AlertmanagerSilence) => void;
}) {
  const [showExpired, setShowExpired] = useState(false);
  const visible = silences.filter(
    silence => showExpired || silence.state !== 'expired',
  );
  return (
    <Stack spacing={2}>
      <Box sx={PANEL}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ color: '#c7c7c7', fontSize: 12 }}>
            Show expired silences
          </Typography>
          <Switch
            size="small"
            checked={showExpired}
            onChange={event => setShowExpired(event.target.checked)}
          />
        </Stack>
      </Box>
      <Box sx={PANEL}>
        {visible.length === 0 ? (
          <Typography sx={{ color: '#c7c7c7', fontSize: 12 }}>
            No silences to show.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Matchers</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell>Created by</TableCell>
                  <TableCell>Starts</TableCell>
                  <TableCell>Ends</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map(silence => (
                  <TableRow key={silence.id} hover>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <SilenceMatchers silence={silence} />
                    </TableCell>
                    <TableCell>
                      <Badge
                        label={silence.state ?? 'active'}
                        color={stateColor(silence.state)}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography sx={{ color: '#f0f0f0', fontSize: 12 }}>
                        {silence.comment ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#c7c7c7', fontSize: 12 }}>
                        {silence.createdBy ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#c7c7c7', fontSize: 11 }}>
                        {formatTime(silence.startsAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#c7c7c7', fontSize: 11 }}>
                        {formatTime(silence.endsAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {silence.state === 'expired' ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => onRecreate(silence)}
                        >
                          Recreate
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => onExpire(silence)}
                        >
                          Expire
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Stack>
  );
}

// ─── top-level panel ─────────────────────────────────────────────────────────

export function AlertsPanel({
  api,
  cluster,
  namespace,
}: {
  api: KubernetesConsoleApi;
  cluster: string;
  namespace: string;
}) {
  const [subTab, setSubTab] = useState<'alerts' | 'silences'>('alerts');
  const [capability, setCapability] = useState<AlertmanagerCapability>();
  const [alerts, setAlerts] = useState<AlertmanagerAlert[]>([]);
  const [silences, setSilences] = useState<AlertmanagerSilence[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formPrefill, setFormPrefill] = useState<SilenceFormPrefill>();
  const [confirmExpire, setConfirmExpire] =
    useState<AlertmanagerSilence | null>(null);
  const [expiring, setExpiring] = useState(false);
  const [toast, setToast] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const cap = await api.alertmanagerCapability(cluster);
      setCapability(cap);
      if (!cap.capability) {
        setAlerts([]);
        setSilences([]);
        return;
      }
      const [alertsResult, silencesResult] = await Promise.all([
        api.namespaceAlertmanagerAlerts(cluster, namespace),
        api.namespaceSilences(cluster, namespace),
      ]);
      setAlerts(alertsResult.alerts);
      setSilences(silencesResult.silences);
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : String(loadError),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cluster && namespace) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cluster, namespace]);

  const openSilenceFor = (labels: Record<string, string>) => {
    setFormPrefill({ matchers: matchersFromLabels(labels) });
    setFormOpen(true);
  };
  const openEmptySilence = () => {
    setFormPrefill(undefined);
    setFormOpen(true);
  };
  const openRecreate = (silence: AlertmanagerSilence) => {
    setFormPrefill(prefillFromSilence(silence));
    setFormOpen(true);
  };

  const expire = async () => {
    if (!confirmExpire) return;
    setExpiring(true);
    try {
      await api.expireSilence(cluster, confirmExpire.id);
      setToast({ severity: 'success', message: 'Silence expired.' });
      setConfirmExpire(null);
      await load();
    } catch (expireError) {
      setToast({
        severity: 'error',
        message:
          expireError instanceof Error
            ? expireError.message
            : String(expireError),
      });
    } finally {
      setExpiring(false);
    }
  };

  if (loading && !capability) {
    return <CircularProgress size={24} />;
  }

  if (capability && !capability.capability) {
    return (
      <Alert severity="info">
        Alertmanager isn't available for this cluster
        {capability.reason ? `: ${capability.reason}` : '.'}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          size="small"
          variant={subTab === 'alerts' ? 'contained' : 'outlined'}
          onClick={() => setSubTab('alerts')}
        >
          Firing alerts{alerts.length ? ` (${alerts.length})` : ''}
        </Button>
        <Button
          size="small"
          variant={subTab === 'silences' ? 'contained' : 'outlined'}
          onClick={() => setSubTab('silences')}
        >
          Silences{silences.length ? ` (${silences.length})` : ''}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <CircularProgress size={20} />}
      {!loading && subTab === 'alerts' && (
        <AlertsList
          alerts={alerts}
          onSilence={openSilenceFor}
          onCreateSilence={openEmptySilence}
        />
      )}
      {!loading && subTab === 'silences' && (
        <SilencesList
          silences={silences}
          onExpire={silence => setConfirmExpire(silence)}
          onRecreate={openRecreate}
        />
      )}

      <SilenceForm
        api={api}
        cluster={cluster}
        namespace={namespace}
        open={formOpen}
        prefill={formPrefill}
        maxDurationSeconds={capability?.maxDurationSeconds}
        onClose={() => setFormOpen(false)}
        onCreated={() => {
          setFormOpen(false);
          setToast({ severity: 'success', message: 'Silence created.' });
          void load();
        }}
      />

      <Dialog
        open={confirmExpire !== null}
        onClose={() => setConfirmExpire(null)}
      >
        <DialogTitle>Expire silence?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This immediately re-enables notifications for any alerts matching
            this silence. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmExpire(null)} disabled={expiring}>
            Cancel
          </Button>
          <Button color="error" onClick={expire} disabled={expiring}>
            {expiring ? 'Expiring…' : 'Expire'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}
